/**
 * Global seismic energy from recent USGS earthquake activity (M4.5+ week).
 * Awareness only — not a safety or scientific alert system.
 */

export type NatureMood =
  | 'quiet'
  | 'low'
  | 'moderate'
  | 'high'
  | 'very-high';

export interface MotherNatureReport {
  mood: NatureMood;
  label: string;
  emoji: string;
  level: 1 | 2 | 3 | 4 | 5;
  summary: string;
  quakeCount45: number;
  quakeCount50: number;
  quakeCount60: number;
  maxMagnitude: number | null;
  error?: string;
}

const USGS_WEEK =
  'https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/4.5_week.geojson';

interface UsgsFeature {
  properties?: { mag?: number; place?: string; title?: string };
}

function moodFromStats(
  count45: number,
  count50: number,
  count60: number,
  maxMag: number
): { mood: NatureMood; level: 1 | 2 | 3 | 4 | 5; label: string; emoji: string; summary: string } {
  let score = 0;
  score += Math.min(count45, 40) * 1.2;
  score += count50 * 4;
  score += count60 * 12;
  if (maxMag >= 7) score += 45;
  else if (maxMag >= 6.5) score += 30;
  else if (maxMag >= 6) score += 18;
  else if (maxMag >= 5.5) score += 8;

  if (score < 18) {
    return {
      mood: 'quiet',
      level: 1,
      label: 'Quiet',
      emoji: '🌍',
      summary: 'Relatively few large earthquakes worldwide this week.',
    };
  }
  if (score < 45) {
    return {
      mood: 'low',
      level: 2,
      label: 'Low',
      emoji: '🌍',
      summary: 'Typical background activity: scattered M4.5+ events.',
    };
  }
  if (score < 85) {
    return {
      mood: 'moderate',
      level: 3,
      label: 'Moderate',
      emoji: '🌍',
      summary: 'Elevated activity compared with a quiet week — more or larger events.',
    };
  }
  if (score < 130) {
    return {
      mood: 'high',
      level: 4,
      label: 'High',
      emoji: '🌍',
      summary: 'Strong activity this week: many sizable quakes or at least one major event.',
    };
  }
  return {
    mood: 'very-high',
    level: 5,
    label: 'Very high',
    emoji: '🌍',
    summary:
      'Unusually high seismic energy this week. Check USGS or local agencies if you are in a risk zone.',
  };
}

export async function fetchMotherNatureReport(): Promise<MotherNatureReport> {
  try {
    const res = await fetch(USGS_WEEK);
    if (!res.ok) throw new Error(`USGS ${res.status}`);
    const data = (await res.json()) as { features?: UsgsFeature[] };
    const features = data.features ?? [];
    let maxMag = 0;
    let count45 = 0;
    let count50 = 0;
    let count60 = 0;
    for (const f of features) {
      const m = f.properties?.mag;
      if (typeof m !== 'number' || Number.isNaN(m)) continue;
      count45++;
      if (m >= 5) count50++;
      if (m >= 6) count60++;
      if (m > maxMag) maxMag = m;
    }
    const maxMagnitude = maxMag > 0 ? Math.round(maxMag * 10) / 10 : null;
    const { mood, level, label, emoji, summary } = moodFromStats(
      count45,
      count50,
      count60,
      maxMag
    );
    return {
      mood,
      level,
      label,
      emoji,
      summary,
      quakeCount45: count45,
      quakeCount50: count50,
      quakeCount60: count60,
      maxMagnitude,
    };
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Unknown error';
    return {
      mood: 'low',
      level: 2,
      label: 'Unavailable',
      emoji: '🌍',
      summary: 'Couldn’t load the USGS earthquake feed.',
      quakeCount45: 0,
      quakeCount50: 0,
      quakeCount60: 0,
      maxMagnitude: null,
      error: msg,
    };
  }
}
